
import React, { useState, useEffect } from 'react';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent, 
  CardFooter 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { LoaderCircle, Bookmark, Search, Clock, MapPin, Circle, Check, X, Trash, Edit, Save, Loader2 } from 'lucide-react';
import { SavedSearch, getSavedSearches, deleteSavedSearch, createSavedSearch, updateSavedSearch, markSearchAsUsed } from '@/services/savedSearchService';
import { formatDistanceToNow } from 'date-fns';

interface SavedSearchesProps {
  onLoadSearch: (search: SavedSearch) => void;
  currentSearch: {
    query?: string;
    location?: string;
    radius: number;
  };
  resultCount?: number;
}

const SavedSearches: React.FC<SavedSearchesProps> = ({ 
  onLoadSearch, 
  currentSearch,
  resultCount 
}) => {
  const { toast } = useToast();
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  const [newSearchName, setNewSearchName] = useState('');
  const [editingSearch, setEditingSearch] = useState<SavedSearch | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Load saved searches when component mounts
  useEffect(() => {
    loadSavedSearches();
  }, []);

  const loadSavedSearches = async () => {
    setLoading(true);
    const searches = await getSavedSearches();
    setSavedSearches(searches);
    setLoading(false);
  };

  const handleSaveCurrentSearch = async () => {
    if (!newSearchName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for your saved search.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const searchToSave = {
        name: newSearchName,
        query: currentSearch.query,
        location: currentSearch.location,
        radius: currentSearch.radius,
        result_count: resultCount
      };

      const savedSearch = await createSavedSearch(searchToSave);
      
      if (savedSearch) {
        setSavedSearches(prev => [savedSearch, ...prev]);
        setNewSearchName('');
        
        toast({
          title: "Search Saved",
          description: `"${savedSearch.name}" has been saved and can be loaded again later.`,
        });
      } else {
        throw new Error("Failed to save search");
      }
    } catch (error) {
      toast({
        title: "Save Failed",
        description: "Could not save your search. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSearch = async () => {
    if (!editingSearch) return;
    
    if (!editingSearch.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for your saved search.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const updatedSearch = await updateSavedSearch(editingSearch.id, {
        name: editingSearch.name
      });
      
      if (updatedSearch) {
        setSavedSearches(prev => 
          prev.map(search => search.id === updatedSearch.id ? updatedSearch : search)
        );
        setEditingSearch(null);
        
        toast({
          title: "Search Updated",
          description: `"${updatedSearch.name}" has been updated.`,
        });
      } else {
        throw new Error("Failed to update search");
      }
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Could not update your search. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSearch = async (id: string) => {
    setDeleting(prev => ({ ...prev, [id]: true }));
    
    try {
      const success = await deleteSavedSearch(id);
      
      if (success) {
        setSavedSearches(prev => prev.filter(search => search.id !== id));
        
        toast({
          title: "Search Deleted",
          description: "The saved search has been deleted.",
        });
      } else {
        throw new Error("Failed to delete search");
      }
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: "Could not delete the saved search. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleLoadSearch = async (search: SavedSearch) => {
    onLoadSearch(search);
    await markSearchAsUsed(search.id);
    // Update the search in local state to reflect the updated last_used_at
    setSavedSearches(prev => 
      prev.map(s => s.id === search.id ? {...s, last_used_at: new Date().toISOString()} : s)
    );
    setSheetOpen(false);
  };

  // Format relative time for display
  const formatTime = (dateString?: string) => {
    if (!dateString) return 'Never used';
    return `${formatDistanceToNow(new Date(dateString))} ago`;
  };

  // Format radius for display
  const formatRadius = (meters: number) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
  };

  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1">
          <Bookmark className="h-4 w-4" />
          Saved Searches
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle>Saved Searches</SheetTitle>
        </SheetHeader>
        
        {/* Save current search section */}
        <div className="mb-6 border-b pb-6">
          <h3 className="text-sm font-medium mb-2">Save Current Search</h3>
          <div className="space-y-2">
            <Input
              placeholder="Enter a name for this search"
              value={newSearchName}
              onChange={(e) => setNewSearchName(e.target.value)}
              className="w-full"
            />
            <div className="text-xs text-muted-foreground">
              <div className="flex items-center gap-1 mb-1">
                <Search className="h-3 w-3" />
                <span>Query: {currentSearch.query || '(none)'}</span>
              </div>
              <div className="flex items-center gap-1 mb-1">
                <MapPin className="h-3 w-3" />
                <span>Location: {currentSearch.location || '(none)'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Circle className="h-3 w-3" />
                <span>Radius: {formatRadius(currentSearch.radius)}</span>
              </div>
            </div>
          </div>
          <Button 
            onClick={handleSaveCurrentSearch} 
            disabled={saving || !currentSearch.query && !currentSearch.location}
            className="w-full mt-2"
            size="sm"
          >
            {saving ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-3 w-3 mr-1" />
                Save This Search
              </>
            )}
          </Button>
        </div>
        
        {/* List of saved searches */}
        <div>
          <h3 className="text-sm font-medium mb-2">Your Saved Searches</h3>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <LoaderCircle className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : savedSearches.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <Bookmark className="h-10 w-10 mx-auto mb-2 opacity-20" />
              <p>You don't have any saved searches yet.</p>
              <p>Save your current search to access it later.</p>
            </div>
          ) : (
            <div className="space-y-2 mt-2">
              {savedSearches.map(search => (
                <Card key={search.id} className="relative">
                  {editingSearch?.id === search.id ? (
                    <CardContent className="p-4">
                      <Input
                        value={editingSearch.name}
                        onChange={(e) => setEditingSearch({ ...editingSearch, name: e.target.value })}
                        autoFocus
                        className="mb-2"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button 
                          size="sm" 
                          onClick={handleUpdateSearch} 
                          disabled={saving}
                          className="flex-1"
                        >
                          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                          Save
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => setEditingSearch(null)}
                          className="flex-1"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  ) : (
                    <>
                      <CardHeader className="p-4 pb-0">
                        <CardTitle className="text-base flex items-center justify-between">
                          <span className="truncate">{search.name}</span>
                          <div className="flex items-center gap-1 ml-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => setEditingSearch(search)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteSearch(search.id)}
                              disabled={deleting[search.id]}
                            >
                              {deleting[search.id] ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Trash className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 pt-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1 mb-1">
                          <Search className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{search.query || '(none)'}</span>
                        </div>
                        <div className="flex items-center gap-1 mb-1">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{search.location || '(none)'}</span>
                        </div>
                        <div className="flex items-center gap-1 justify-between">
                          <div className="flex items-center">
                            <Circle className="h-3 w-3 mr-1" />
                            <span>{formatRadius(search.radius)}</span>
                          </div>
                          {search.result_count !== null && search.result_count !== undefined && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0">
                              {search.result_count} results
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-2 text-[10px]">
                          <Clock className="h-3 w-3" />
                          <span>Last used: {formatTime(search.last_used_at)}</span>
                        </div>
                      </CardContent>
                      <CardFooter className="p-4 pt-0">
                        <Button 
                          className="w-full" 
                          variant="default" 
                          size="sm"
                          onClick={() => handleLoadSearch(search)}
                        >
                          Load This Search
                        </Button>
                      </CardFooter>
                    </>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SavedSearches;
